import { useEffect, useMemo } from "react";

import { create, StoreApi } from "zustand";

type PersistedParamsConfig = {
  storageKeyFn: (scope: string) => string;
  isManaged: (key: string) => boolean;
};

type PersistedParamsState = {
  scope: string | null;
  params: Record<string, string>;
};

type PersistedParamsActions = {
  initialize: (
    scope: string,
    managedUrlParams: Record<string, string>,
    storedParams: Record<string, string> | null,
  ) => void;
  setParams: (params: Record<string, string>) => void;
  clearParams: () => void;
};

type PersistedParamsStore = ZustandStore<
  PersistedParamsState,
  PersistedParamsActions
>;

type UsePersistedParamsResult = {
  params: Record<string, string>;
  actions: {
    setParams: (params: Record<string, string>) => void;
    clearParams: () => void;
  };
};

const readManagedParams = (
  searchParams: URLSearchParams,
  isManaged: (key: string) => boolean,
): Record<string, string> =>
  Object.fromEntries(
    Array.from(searchParams.entries()).filter(([key]) => isManaged(key)),
  );

const readLocalStorage = (key: string): Record<string, string> | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Record<string, string>) : null;
  } catch {
    return null;
  }
};

const writeLocalStorage = (
  key: string,
  params: Record<string, string>,
): void => {
  localStorage.setItem(key, JSON.stringify(params));
};

const removeLocalStorage = (key: string): void => {
  localStorage.removeItem(key);
};

const syncParamsToUrl = (
  params: Record<string, string>,
  isManaged: (key: string) => boolean,
): void => {
  const url = new URL(window.location.href);

  Array.from(url.searchParams.keys())
    .filter((key) => isManaged(key))
    .forEach((key) => url.searchParams.delete(key));

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.set(key, value);
  });

  window.history.replaceState(window.history.state, "", url);
};

type UsePersistedParamsOptions = {
  /** Scoping identifier (e.g. queueId) that determines which localStorage entry to use.
   *  When undefined, the hook is inert: returns empty params, skips localStorage and URL sync. */
  key?: string;
};

/**
 * Creates a scoped parameter store that keeps URL search params, localStorage, and
 * in-memory Zustand state in sync.
 *
 * Solves a common UI problem: when a user applies filters/sorting on a scoped page
 * (e.g. a specific queue), those preferences should:
 *   1. Reflect in the URL (shareable links)
 *   2. Persist across navigations (localStorage, keyed by scope)
 *   3. Be available as reactive state for queries and components
 *
 * Hydration priority: URL params > localStorage > empty. This means a shared link
 * with filters in the URL always wins over locally saved preferences.
 *
 * @example
 * // 1. Create a store for your feature
 * const { usePersistedParams, store } = createPersistedParamsStore({
 *   storageKeyFn: (queueId) => `cases-queue-params/${queueId}`,
 *   isManaged: (key) => key.startsWith("filter[") || key === "sort",
 * });
 *
 * // 2. Use in a component — key scopes to the current entity
 * const { params, actions } = usePersistedParams({ key: queueId });
 *
 * // 3. When key is undefined (entity not yet loaded), the hook is inert:
 * //    returns empty params, no localStorage reads/writes, no URL manipulation
 * const { params } = usePersistedParams({ key: undefined });
 */
export const createPersistedParamsStore = (
  config: PersistedParamsConfig,
): {
  usePersistedParams: (
    options: UsePersistedParamsOptions,
  ) => UsePersistedParamsResult;
  store: StoreApi<PersistedParamsStore>;
} => {
  const store = create<PersistedParamsStore>()((set) => ({
    scope: null,
    params: {},
    actions: {
      initialize: (scope, managedUrlParams, storedParams) => {
        const hasUrlParams = Object.keys(managedUrlParams).length > 0;

        if (hasUrlParams) {
          writeLocalStorage(config.storageKeyFn(scope), managedUrlParams);
          set({ scope, params: managedUrlParams });
        } else if (storedParams && Object.keys(storedParams).length > 0) {
          set({ scope, params: storedParams });
        } else {
          set({ scope, params: {} });
        }
      },
      setParams: (params) => {
        set((state) => {
          if (state.scope) {
            writeLocalStorage(config.storageKeyFn(state.scope), params);
          }
          return { params };
        });
      },
      clearParams: () => {
        set((state) => {
          if (state.scope) {
            removeLocalStorage(config.storageKeyFn(state.scope));
          }
          return { params: {} };
        });
      },
    },
  }));

  store.subscribe((state, prevState) => {
    if (state.params === prevState.params) return;
    if (!state.scope) return;

    syncParamsToUrl(state.params, config.isManaged);
  });

  const resolveParams = (scope: string): Record<string, string> => {
    const searchParams = new URLSearchParams(window.location.search);
    const managedUrlParams = readManagedParams(searchParams, config.isManaged);
    if (Object.keys(managedUrlParams).length > 0) return managedUrlParams;

    const storedParams = readLocalStorage(config.storageKeyFn(scope));
    if (storedParams && Object.keys(storedParams).length > 0)
      return storedParams;

    return {};
  };

  const usePersistedParams = (
    options: UsePersistedParamsOptions,
  ): UsePersistedParamsResult => {
    const { key: scope } = options;
    const storeParams = store((s) => s.params);
    const storeScope = store((s) => s.scope);
    const actions = store((s) => s.actions);

    const params = useMemo(() => {
      if (!scope) return {};
      if (storeScope === scope) return storeParams;
      return resolveParams(scope);
    }, [scope, storeScope, storeParams]);

    useEffect(() => {
      if (!scope) return;
      if (store.getState().scope === scope) return;

      const searchParams = new URLSearchParams(window.location.search);
      const managedUrlParams = readManagedParams(
        searchParams,
        config.isManaged,
      );
      const storedParams = readLocalStorage(config.storageKeyFn(scope));
      actions.initialize(scope, managedUrlParams, storedParams);
    }, [scope, storeScope, actions.initialize]);

    return {
      params,
      actions: {
        setParams: actions.setParams,
        clearParams: actions.clearParams,
      },
    };
  };

  return { usePersistedParams, store };
};
